import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Stack,
  Tooltip,
  IconButton,
  Modal,
  Button,
  TextField,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import dayjs from "dayjs";
import baseURL from "../../../config";
import axios from "axios";
import { toast } from "react-toastify";

const SignOffModal = ({ signoffDetails }) => {
  if (!signoffDetails || signoffDetails.length === 0) return null;

  const [openModal, setOpenModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCrewName, setSelectedCrewName] = useState("");
  const [selectedSignOff, setSelectedSignOff] = useState(null);
  const [formData, setFormData] = useState({
    date: "",
    portName: "",
    portCountry: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const userID = localStorage.getItem("user id");

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEditClick = (item, crewName) => {
    setSelectedSignOff(item);
    setFormData({
      date: item.contractStatusAfterForm1?.signOff?.[0]?.date?.split("T")[0] || "",
      portName: item.contractStatusAfterForm1?.signOff?.[0]?.portName || "",
      portCountry: item.contractStatusAfterForm1?.signOff?.[0]?.portCountry || "",
    });
    setSelectedCrewName(crewName);
    setEditModalOpen(true);
  };

  const handleOpenSignOffFiles = (signOffData) => {
    const documents = [];

    if (signOffData.cdcStamppageFile?.filePath) {
      documents.push({
        name: "CDC Stamp Page",
        filePath: `${baseURL}/${signOffData.cdcStamppageFile.filePath.replace(/\\/g, "/")}`,
      });
    }

    if (signOffData.passport?.filePath) {
      documents.push({
        name: "Passport",
        filePath: `${baseURL}/${signOffData.passport.filePath.replace(/\\/g, "/")}`,
      });
    }

    if (signOffData.salaryclose?.filePath) {
      documents.push({
        name: "Salary Close",
        filePath: `${baseURL}/${signOffData.salaryclose.filePath.replace(/\\/g, "/")}`,
      });
    }

    setSelectedDocuments(documents);
    setOpenModal(true);
  };

  const handleSave = async () => {

    if (!formData.date || !formData.portName.trim() || !formData.portCountry.trim()) {
      toast.error("All fields are required.");
      return;
    }


    if (!selectedSignOff) {
      toast.error("No contract selected!");
      return;
    }

    setIsSaving(true);

    const token = localStorage.getItem("token");

    const dataToSend = {
      creatorId: userID,
      crewId: selectedSignOff.contractDetails?.crewdetails?._id || "",
      contractId: selectedSignOff.contractDetails?._id || "",
      departure: [{
        date: formData.date,
        portName: formData.portName,
        country: formData.portCountry,
      }]
    };

    try {
      const response = await axios.post(`${baseURL}/api/statusAfterForm1`, dataToSend, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      toast.success("Data saved successfully!");
      setEditModalOpen(false);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error saving data:", error.response?.data || error.message);
      toast.error("Failed to save data. Please try again.");
    } finally {
      setIsSaving(false); // Hide loader
    }
  };

  return (
    <>
      <Box p={3}>
        <Typography variant="h6">Sign-Off Records</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No.</TableCell>
                <TableCell>Contract Info</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {signoffDetails.map((item, index) => {
                const contract = item.contractDetails;
                const signOff = item.contractStatusAfterForm1?.signOff?.[0] || {};
                const formattedDate = signOff.date ? dayjs(signOff.date).format("DD-MM-YYYY") : "N/A";
                const signOn = item.contractStatusAfterForm1?.signOn?.[0] || {};
                const formattedSignOn = signOn?.date ? dayjs(signOn.date).format("DD-MM-YYYY") : "N/A";
                const coc = item.contractStatusAfterForm1?.coc?.[0] || {};
                const formattedCOC = coc?.date ? dayjs(coc.date).format("DD-MM-YYYY") : "N/A";

                const crewName = contract?.crewdetails?.name || "N/A";
                const rankName = contract?.rank?.rankname || "N/A";

                return (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip label={`Crew: ${crewName}`} />
                        <Chip label={`Rank: ${rankName}`} />
                        <Chip label={`COC: ${formattedCOC}`} />
                        <Chip label={`SignOn: ${formattedSignOn}`} />
                        <Chip label={`SignOff: ${formattedDate}`} />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {signOff && (
                        <>
                          {(signOff.cdcStamppageFile?.filePath ||
                            signOff.passport?.filePath ||
                            signOff.salaryclose?.filePath) && (
                              <Tooltip title="View Documents">
                                <IconButton onClick={() => handleOpenSignOffFiles(signOff)}>
                                  <PictureAsPdfIcon sx={{ color: "red" }} />
                                </IconButton>
                              </Tooltip>
                            )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleEditClick(item, crewName)}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Document Modal */}
        <Modal open={openModal} onClose={() => setOpenModal(false)}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: "60%", md: "50%" },
              maxHeight: "80vh",
              bgcolor: "white",
              boxShadow: 24,
              p: 3,
              borderRadius: 2,
              overflowY: "auto",
            }}
          >
            <Typography variant="h6" align="center" mb={2}>
              Sign-Off Documents
            </Typography>
            {selectedDocuments.length > 0 ? (
              selectedDocuments.map((doc, index) => (
                <Paper
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    boxShadow: 2,
                    "&:hover": { bgcolor: "#f5f5f5" },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <PictureAsPdfIcon sx={{ color: "red", mr: 1 }} />
                    <Typography>{doc.name}</Typography>
                  </Box>
                  <Button
                    href={doc.filePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    size="small"
                  >
                    Download
                  </Button>
                </Paper>
              ))
            ) : (
              <Typography align="center">No documents available</Typography>
            )}
            <Box display="flex" justifyContent="center" mt={2}>
              <Button variant="contained" color="error" onClick={() => setOpenModal(false)}>
                Close
              </Button>
            </Box>
          </Box>
        </Modal>

        {/* Edit Modal */}
        <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
          <Paper
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 400,
              p: 3,
              borderRadius: 2,
            }}
          >
            <Typography variant="h6" align="center" gutterBottom>
              Enter Arrival - <span style={{ color: "green" }}>{selectedCrewName}</span>
            </Typography>
            <TextField
              fullWidth
              label="Date"
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Port Name"
              name="portName"
              value={formData.portName}
              onChange={handleChange}
              margin="dense"
            />
            <TextField
              fullWidth
              label="Country"
              name="portCountry"
              value={formData.portCountry}
              onChange={handleChange}
              margin="dense"
            />
            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button onClick={() => setEditModalOpen(false)} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleSave} variant="contained" color="primary" disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </Box>
          </Paper>
        </Modal>
      </Box>
    </>
  );
};

export default SignOffModal;
